import { useMemo } from 'react'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

import type { FeatureFlagKeyType } from '@scrambl/shared'

/**
 * Maps a searchable module-search type to the feature flag that gates it.
 *
 * Record-reference pickers (HierarchicalModuleSearch, MultiModuleSearchField)
 * accept a hardcoded `allowedModuleTypes` list. Without gating, a picker on a
 * personal account (or a business org that disabled a module) offers business
 * records — the exact leak this map closes. It mirrors the per-module gating
 * OmniSearch and the sidebar already apply.
 *
 * Types intentionally absent (PROJECT-less children like TASK_GROUP are covered;
 * ORGANIZATION) are structural / always available and never filtered. `undefined`
 * (type not in this map) = always shown.
 *
 */
export const MODULE_SEARCH_TYPE_FEATURE_FLAG: Record<
  string,
  FeatureFlagKeyType
> = {
  ENTITY: 'ENTITIES',
  PROJECT: 'PROJECTS',
  TASK: 'TASKS',
  TASK_GROUP: 'TASKS',
  TICKET: 'TICKETS',
  DEAL: 'DEALS',
  CONTACT: 'CONTACTS',
  EVENT: 'EVENTS',
  CONTRACT: 'CONTRACTS',
  INVOICE: 'INVOICES',
  INVENTORY: 'INVENTORY',
  SUPPLIER: 'SUPPLIERS',
  NOTE: 'NOTES',
  DOCUMENT: 'DOCUMENTS',
  CUSTOM_COLLECTION: 'COLLECTIONS',
}

/**
 * Filters a picker's `allowedModuleTypes` down to the types whose gating feature
 * flag is enabled for the current org. Callers should derive the placeholder,
 * the searched types, and the rendered result groups from this filtered list so
 * every surface stays consistent (no business types offered, searched, or shown
 * on a personal account).
 *
 * Fails open: while the flag bulk-fetch is still pending (`flagsData` undefined)
 * the full list is returned, matching the codebase convention of failing safe
 * toward the full business experience rather than briefly hiding modules.
 */
export function useVisibleModuleTypes(allowedTypes: string[] = []): string[] {
  const { checkFeatureFlag, flagsData } = useFeatureFlags()
  const flagsLoaded = !!flagsData
  const allowedTypesKey = allowedTypes.join(',')
  return useMemo(
    () => {
      const supportedTypes = allowedTypes
      if (!flagsLoaded) return supportedTypes
      return supportedTypes.filter(type => {
        const flag = MODULE_SEARCH_TYPE_FEATURE_FLAG[type]
        return flag ? checkFeatureFlag(flag) : true
      })
    },
    // allowedTypesKey captures the input array's contents; checkFeatureFlag and
    // flagsLoaded capture the current flag state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedTypesKey, checkFeatureFlag, flagsLoaded],
  )
}
